import React, { useMemo, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  ANNOUNCEMENT_DISPLAY_MODE_OPTIONS,
  ANNOUNCEMENT_STATUS_OPTIONS,
  ANNOUNCEMENT_TYPE_OPTIONS,
  toSelectOptions,
} from "../../utils/announcementUtils";

const AnnouncementForm = ({
  formValues,
  onChange,
  onSubmit,
  saving = false,
  readOnly = false,
  roleOptions = [],
  departmentOptions = [],
  userOptions = [],
  pageOptions = [],
}) => {
  const isDarkMode =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const selectStyles = useMemo(() => getSelectStyles(isDarkMode), [isDarkMode]);
  const roleValue = toSelectOptions(formValues.audience.roles);
  const departmentValue = toSelectOptions(formValues.audience.departments);
  const userValue = formValues.audience.userIds
    .map((value) => userOptions.find((option) => option.value === value))
    .filter(Boolean);
  const pageValue = toSelectOptions(formValues.pages);

  const handleAudienceArrayChange = (field, options) => {
    onChange("audience", {
      ...formValues.audience,
      [field]: Array.isArray(options) ? options.map((option) => option.value) : [],
    });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Announcement Details
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            กำหนดหัวข้อ ข้อความ ประเภท และช่วงเวลาการแสดงผล
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Field label="Title" required>
            <input
              type="text"
              value={formValues.title}
              onChange={(event) => onChange("title", event.target.value)}
              disabled={readOnly}
              className={inputClassName}
              placeholder="หัวข้อประกาศ"
            />
          </Field>

          <Field label="Priority">
            <input
              type="number"
              value={formValues.priority}
              onChange={(event) => onChange("priority", event.target.value)}
              disabled={readOnly}
              className={inputClassName}
            />
          </Field>

          <Field label="Type">
            <select
              value={formValues.type}
              onChange={(event) => onChange("type", event.target.value)}
              disabled={readOnly}
              className={inputClassName}
            >
              {ANNOUNCEMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Status">
            <select
              value={formValues.status}
              onChange={(event) => onChange("status", event.target.value)}
              disabled={readOnly}
              className={inputClassName}
            >
              {ANNOUNCEMENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Display Mode">
            <select
              value={formValues.displayMode}
              onChange={(event) => onChange("displayMode", event.target.value)}
              disabled={readOnly}
              className={inputClassName}
            >
              {ANNOUNCEMENT_DISPLAY_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Start At">
            <DateTimeInput
              value={formValues.startAt}
              onChange={(event) => onChange("startAt", event.target.value)}
              disabled={readOnly}
            />
          </Field>

          <Field label="End At">
            <DateTimeInput
              value={formValues.endAt}
              onChange={(event) => onChange("endAt", event.target.value)}
              disabled={readOnly}
            />
          </Field>

          <div className="lg:col-span-2">
            <Field label="Message" required>
              <textarea
                rows={6}
                value={formValues.message}
                onChange={(event) => onChange("message", event.target.value)}
                disabled={readOnly}
                className={`${inputClassName} py-3`}
                placeholder="ข้อความที่ต้องการแสดงใน popup"
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Audience
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            ระบุผู้ใช้ที่ต้องเห็นประกาศนี้ หากเปิด Show to all ระบบจะมองว่าแสดงกับทุกคน
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <label className="inline-flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
              <input
                type="checkbox"
                checked={formValues.audience.showToAll}
                onChange={(event) =>
                  onChange("audience", {
                    ...formValues.audience,
                    showToAll: event.target.checked,
                  })
                }
                disabled={readOnly}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Show to all users
            </label>
          </div>

          <Field label="Roles">
            <Select
              isMulti
              isDisabled={readOnly}
              options={roleOptions}
              value={roleValue}
              onChange={(options) => handleAudienceArrayChange("roles", options)}
              styles={selectStyles}
              classNamePrefix="announcement-select"
              placeholder="เลือก role"
            />
          </Field>

          <Field label="Departments">
            <CreatableSelect
              isMulti
              isDisabled={readOnly}
              options={departmentOptions}
              value={departmentValue}
              onChange={(options) =>
                handleAudienceArrayChange("departments", options)
              }
              styles={selectStyles}
              classNamePrefix="announcement-select"
              placeholder="เลือกหรือพิมพ์ department"
            />
          </Field>

          <div className="lg:col-span-2">
            <Field label="Users">
              <Select
                isMulti
                isDisabled={readOnly}
                options={userOptions}
                value={userValue}
                onChange={(options) => handleAudienceArrayChange("userIds", options)}
                styles={selectStyles}
                classNamePrefix="announcement-select"
                placeholder="เลือกผู้ใช้เป็นรายคน"
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Behavior
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            ตั้งค่าการแสดง popup และกำหนดปุ่ม CTA หากต้องการ
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <Field label="Pages">
              <CreatableSelect
                isMulti
                isDisabled={readOnly}
                options={pageOptions}
                value={pageValue}
                onChange={(options) =>
                  onChange(
                    "pages",
                    Array.isArray(options) ? options.map((option) => option.value) : []
                  )
                }
                styles={selectStyles}
                classNamePrefix="announcement-select"
                placeholder="เลือกหรือพิมพ์ route path เช่น /travel-expense/request"
              />
            </Field>
          </div>

          <Field label="CTA Label">
            <input
              type="text"
              value={formValues.cta.label}
              onChange={(event) =>
                onChange("cta", {
                  ...formValues.cta,
                  label: event.target.value,
                })
              }
              disabled={readOnly}
              className={inputClassName}
              placeholder="เช่น ดูรายละเอียด"
            />
          </Field>

          <Field label="CTA URL">
            <input
              type="text"
              value={formValues.cta.url}
              onChange={(event) =>
                onChange("cta", {
                  ...formValues.cta,
                  url: event.target.value,
                })
              }
              disabled={readOnly}
              className={inputClassName}
              placeholder="/announcements/maintenance-july-2026 หรือ https://..."
            />
          </Field>

          <div className="lg:col-span-2 grid grid-cols-1 gap-3 md:grid-cols-3">
            <ToggleField
              label="Dismissible"
              checked={formValues.dismissible}
              onChange={(checked) => onChange("dismissible", checked)}
              disabled={readOnly}
            />
            <ToggleField
              label="Requires acknowledgement"
              checked={formValues.requiresAcknowledgement}
              onChange={(checked) => onChange("requiresAcknowledgement", checked)}
              disabled={readOnly}
            />
            <ToggleField
              label="Show once per user"
              checked={formValues.showOncePerUser}
              onChange={(checked) => onChange("showOncePerUser", checked)}
              disabled={readOnly}
            />
          </div>
        </div>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="submit"
          disabled={readOnly || saving}
          className="inline-flex items-center justify-center rounded-xl bg-[#00ADB5] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01969D] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "กำลังบันทึก..." : "บันทึกประกาศ"}
        </button>
      </div>
    </form>
  );
};

const inputClassName =
  "w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:disabled:bg-gray-700";

const Field = ({ label, required = false, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </span>
    {children}
  </label>
);

const ToggleField = ({ label, checked, onChange, disabled = false }) => (
  <label className="inline-flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      disabled={disabled}
      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
    />
    {label}
  </label>
);

const DateTimeInput = ({ value, onChange, disabled = false }) => {
  const inputRef = useRef(null);

  const openPicker = () => {
    if (disabled) return;
    try {
      inputRef.current?.showPicker?.();
    } catch (error) {
      // Some browsers only allow showPicker during a direct user gesture.
    }
  };

  return (
    <input
      ref={inputRef}
      type="datetime-local"
      value={value}
      onChange={onChange}
      onClick={openPicker}
      disabled={disabled}
      className={`${inputClassName} dark:[color-scheme:dark]`}
    />
  );
};

const getSelectStyles = (isDarkMode) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? "#22c55e" : isDarkMode ? "#4b5563" : "#d1d5db",
    backgroundColor: isDarkMode ? "#111827" : "#ffffff",
    boxShadow: "none",
    "&:hover": {
      borderColor: state.isFocused ? "#22c55e" : isDarkMode ? "#6b7280" : "#9ca3af",
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: isDarkMode ? "#111827" : "#ffffff",
    color: isDarkMode ? "#f3f4f6" : "#111827",
    zIndex: 30,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#14b8a6"
      : state.isFocused
      ? isDarkMode
        ? "#1f2937"
        : "#f3f4f6"
      : isDarkMode
      ? "#111827"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : isDarkMode ? "#f3f4f6" : "#111827",
  }),
  singleValue: (base) => ({
    ...base,
    color: isDarkMode ? "#f3f4f6" : "#111827",
  }),
  input: (base) => ({
    ...base,
    color: isDarkMode ? "#f3f4f6" : "#111827",
  }),
  placeholder: (base) => ({
    ...base,
    color: isDarkMode ? "#9ca3af" : "#6b7280",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: isDarkMode ? "#164e63" : "#ecfeff",
    borderRadius: 9999,
    paddingInline: 4,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: isDarkMode ? "#cffafe" : "#155e75",
    fontWeight: 500,
  }),
});

export default AnnouncementForm;
